import { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSavingGoalsStore } from '@flows.cash/core';
import { useTransactionStore } from '@flows.cash/core';
import { useAuthStore } from '@flows.cash/core';
import { useAccountsStore } from '@flows.cash/core';
import { Transaction } from '@flows.cash/types';
import { formatCurrency } from '@/utils/formatters';
import { ArrowDownRight, ArrowRightLeft, ArrowUpRight, Check, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/dateUtils';

interface AssignTransactionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  goal: {
    id: string;
    name: string;
    current_amount: number;
    target_amount: number;
    currency: string;
  };
}

export default function AssignTransactionsDialog({ isOpen, onClose, goal }: AssignTransactionsDialogProps) {
  const { assignTransactionToGoal, isLoading, fetchGoalTransactions } = useSavingGoalsStore();
  const { transactions, fetchTransactions } = useTransactionStore();
  const { user } = useAuthStore();
  const { accounts } = useAccountsStore();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [assignedTransactionIds, setAssignedTransactionIds] = useState<string[]>([]);
  const [assigningTransactionId, setAssigningTransactionId] = useState<string | null>(null);
  
  // Fetch transactions when dialog opens
  useEffect(() => {
    if (isOpen && user) {
      fetchTransactions(user.id, { limit: 100 });
      loadAssignedTransactions();
    }
  }, [isOpen, user]);
  
  const loadAssignedTransactions = async () => {
    if (!goal.id) return;
    
    try {
      const data = await fetchGoalTransactions(goal.id);
      const ids = data.map(gt => gt.transaction_id);
      setAssignedTransactionIds(ids);
    } catch (error) {
      console.error('Error fetching assigned transactions:', error);
    }
  };
  
  const handleAssign = async (transactionId: string) => {
    try {
      setAssigningTransactionId(transactionId);
      await assignTransactionToGoal({ goalId: goal.id, transactionId });
      setAssignedTransactionIds([...assignedTransactionIds, transactionId]);
      toast.success('Transaction assigned to goal successfully');
    } catch (error) {
      toast.error('Failed to assign transaction to goal');
      console.error(error);
    } finally {
      setAssigningTransactionId(null);
    }
  };
  
  // Get the list of saving account IDs
  const savingAccountIds = accounts
    .filter(account => account.type === 'savings' && !account.isArchived)
    .map(account => account.id);
  
  // Filter transactions based on account type, search term, and exclude already assigned ones
  const filteredTransactions = transactions
    .filter((t: Transaction) => 
      // Only include transactions from saving accounts
      savingAccountIds.includes(t.account_id)
    )
    .filter((t: Transaction) => !assignedTransactionIds.includes(t.id))
    .filter((t: Transaction) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        (t.description?.toLowerCase().includes(term)) ||
        (t.notes?.toLowerCase().includes(term))
      );
    });
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-background-secondary dark:bg-background-dark-secondary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl p-6 shadow-sm">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-xl font-semibold text-text-primary dark:text-text-dark-primary bg-gradient-to-r from-primary to-secondary dark:from-primary-dark dark:to-secondary-dark bg-clip-text text-transparent">
            Assign Transactions to Goal
          </DialogTitle>
          <DialogDescription className="text-text-secondary dark:text-text-dark-secondary mt-1">
            Select transactions from saving accounts to assign to {goal.name}
          </DialogDescription>
        </DialogHeader>

        <div className="relative my-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
          <Input
            placeholder="Search transactions"
            className="pl-10 h-11 w-full rounded-xl border border-border dark:border-border-dark bg-background-input dark:bg-background-dark-input focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="max-h-[300px] overflow-y-auto">
          {filteredTransactions.length > 0 ? (
            <div className="space-y-3">
              {filteredTransactions.map((transaction: Transaction) => (
                <div 
                  key={transaction.id} 
                  className="p-3 border border-border dark:border-border-dark bg-background-input/50 dark:bg-background-dark-input/50 rounded-xl flex justify-between items-center"
                >
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center",
                      transaction.type === 'transfer' 
                        ? 'bg-[#6366F1]/10 dark:bg-[#6366F1]/10 text-[#6366F1] dark:text-[#6366F1]'
                        : transaction.type === 'expense' 
                          ? 'bg-negative/10 dark:bg-negative-dark/10 text-negative dark:text-negative-dark' 
                          : 'bg-positive/10 dark:bg-positive-dark/10 text-positive dark:text-positive-dark'
                    )}>
                      {transaction.type === 'transfer' ? (
                        <ArrowRightLeft className="w-4 h-4" />
                      ) : transaction.type === 'expense' ? (
                        <ArrowDownRight className="w-4 h-4" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        {transaction.description || 'No description'}
                      </p>
                      <p className="text-xs text-text-secondary dark:text-text-dark-secondary">
                        {formatDateTime(new Date(transaction.date), {})}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className={cn(
                      "font-medium text-sm",
                      transaction.type === 'income' ? "text-positive dark:text-positive-dark" : 
                      transaction.type === 'expense' ? "text-negative dark:text-negative-dark" : 
                      "text-primary dark:text-primary-dark"
                    )}>
                      {formatCurrency(transaction.amount, transaction.currency)}
                    </p>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full hover:bg-secondary/10 hover:text-secondary dark:hover:bg-primary-dark/10 dark:hover:text-primary-dark"
                      disabled={assigningTransactionId === transaction.id}
                      onClick={() => handleAssign(transaction.id)}
                    >
                      {assigningTransactionId === transaction.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary dark:border-primary-dark" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 my-4 bg-background-input/50 dark:bg-background-dark-input/50 backdrop-blur-xl backdrop-saturate-150 rounded-xl border border-border dark:border-border-dark">
              <Search className="w-12 h-12 mx-auto mb-3 text-text-secondary dark:text-text-dark-secondary opacity-50" />
              <p className="font-medium text-text-primary dark:text-text-dark-primary">No transactions found</p>
              <p className="text-sm mt-1 text-text-secondary dark:text-text-dark-secondary">Try another search or add transactions to your saving accounts</p>
            </div>
          )}
        </div>
        
        <div className="mt-6 flex justify-end">
          <Button
            variant="outline"
            onClick={onClose}
            className="bg-background-secondary dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary border border-border dark:border-border-dark backdrop-blur-xl backdrop-saturate-150 rounded-xl h-11 mr-2"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 